import React, { createContext, useContext, useEffect, useState } from "react";
import { User, LoginRequest } from "../types";
import { Workshop } from "../types/workshop";
import AuthService from "../services/authService";
import {
  Permission,
  UserRole,
  hasPermission as checkPermission,
  hasAnyPermission as checkAnyPermission,
} from "../types/roles";

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<void>;
  updateUser: (user: User) => void;
  isAuthenticated: boolean;
  isAdmin: boolean;
  userRole: UserRole;
  hasPermission: (permission: Permission) => boolean;
  hasAnyPermission: (permissions: Permission[]) => boolean;
  workshops: Workshop[];
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Auto-refresh token every 25 minutes (before 30min expiry)
  useEffect(() => {
    const interval = setInterval(async () => {
      if (user && AuthService.isAuthenticated()) {
        try {
          await refreshToken();
        } catch (error) {
          console.error("Failed to refresh token:", error);
          logout();
        }
      }
    }, 25 * 60 * 1000); // 25 minutes

    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    // Check if user is already logged in on mount
    const checkAuth = async () => {
      try {
        if (AuthService.isAuthenticated()) {
          const currentUser = await AuthService.getCurrentUser();
          setUser(currentUser);
        }
      } catch (error) {
        // Token might be invalid or expired
        console.error("Auth check failed:", error);
        AuthService.removeToken();
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (credentials: LoginRequest): Promise<void> => {
    try {
      const response = await AuthService.loginJson(credentials);
      AuthService.saveToken(response.access_token);

      const currentUser = await AuthService.getCurrentUser();
      setUser(currentUser);

      // Si el usuario tiene workshops asignados, guardar el primero en localStorage
      if (currentUser.workshops && currentUser.workshops.length > 0) {
        const userWorkshop = currentUser.workshops[0];
        localStorage.setItem("selected_workshop", JSON.stringify(userWorkshop));
      }
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    AuthService.removeToken();
    setUser(null);
    // Limpiar el workshop seleccionado al cerrar sesión
    localStorage.removeItem("selected_workshop");
  };

  const refreshToken = async () => {
    try {
      const response = await AuthService.refreshToken();
      AuthService.saveToken(response.access_token);

      // Optionally refresh user data
      const currentUser = await AuthService.getCurrentUser();
      setUser(currentUser);
    } catch (error) {
      console.error("Failed to refresh token:", error);
      throw error;
    }
  };

  const updateUser = (updatedUser: User) => {
    setUser(updatedUser);
  };

  // Get user role
  const getUserRoleValue = (): UserRole => {
    return user?.is_admin ? UserRole.ADMIN : UserRole.INSPECTOR;
  };

  // Check if user has specific permission
  const hasPermissionCheck = (permission: Permission): boolean => {
    if (!user) return false;
    const role = user.is_admin ? "admin" : "inspector";
    return checkPermission(role, permission);
  };

  // Check if user has any of the permissions
  const hasAnyPermissionCheck = (permissions: Permission[]): boolean => {
    if (!user) return false;
    const role = user.is_admin ? "admin" : "inspector";
    return checkAnyPermission(role, permissions);
  };

  const value: AuthContextType = {
    user,
    loading,
    login,
    logout,
    refreshToken,
    updateUser,
    isAuthenticated: !!user,
    isAdmin: user?.is_admin ?? false,
    userRole: getUserRoleValue(),
    hasPermission: hasPermissionCheck,
    hasAnyPermission: hasAnyPermissionCheck,
    workshops: user?.workshops || [],
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
